import React, { useEffect, useRef } from 'react';
import { knkBookingFields } from '../lib/knkTracking';

/**
 * /demo — Conversion landing page for Meta/IG paid ads.
 *
 * Single goal: book a 15-min discovery call with Adam.
 * No nav, no footer, no external links. Pure conversion.
 *
 * Brand: Navy #21255C bg, Orange #FF6633 CTA, White text.
 *
 * Meta Pixel events fired here:
 *   - ViewContent: on page load
 *   - Lead: on HubSpot Meetings booking confirmation (postMessage)
 *
 * The booking is also reported to /api/track-booking. If the visitor came
 * from a K&K Advertising ad, the request carries the saved Voluum click
 * and the booker's email so the server can send K&K's Lead postback
 * (see docs/knk-voluum-tracking.md).
 *
 * HubSpot meeting link: https://www.roofingbusinesspartner.com/meetings/adamsandlinks/free-ai-consult-
 * Video placeholder: swap src when Adam records the intro video.
 */
const HUBSPOT_MEETING_URL = 'https://meetings.hubspot.com/adamsandlinks/free-ai-consult-';

// Booking messages must come from the meetings iframe: matched by window, or by
// HubSpot's origin / the branded meetings domain in case the iframe redirects.
const MEETINGS_ORIGIN = /^https:\/\/(([a-z0-9-]+\.)*hubspot\.com|www\.roofingbusinesspartner\.com)$/;

const DemoPage = () => {
    const meetingsFrame = useRef(null);
    const bookingReport = useRef({
        id: null, bookedAt: null, recorded: false, pixelSent: false,
        pendingKnk: null, queue: Promise.resolve(),
    });

    // UTM capture — read from URL params on load, store in sessionStorage for attribution
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const utmFields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
        const utmData = {};
        utmFields.forEach(key => {
            const val = params.get(key);
            if (val) utmData[key] = val;
        });
        if (Object.keys(utmData).length > 0) {
            sessionStorage.setItem('roofclaw_utm', JSON.stringify(utmData));
        }
    }, []);

    // Meta Pixel: ViewContent on page load
    useEffect(() => {
        if (window.fbq) {
            window.fbq('track', 'ViewContent', {
                content_name: 'Demo Page',
                content_category: 'Discovery Call',
            });
        }
    }, []);

    // HubSpot Meetings event listener — fires Lead pixel event on booking
    useEffect(() => {
        const handleHubSpotEvent = (e) => {
            const fromMeetingsFrame = e.source != null && e.source === meetingsFrame.current?.contentWindow;
            if (!fromMeetingsFrame && !MEETINGS_ORIGIN.test(e.origin)) return;

            // HubSpot Meetings postMessage event on booking confirmation
            if (e.data?.meetingBookSucceeded || e.data?.type === 'hsCalendar.meeting.booked') {
                // Meta Pixel: Lead event
                const report = bookingReport.current;
                if (!report.pixelSent && window.fbq) {
                    window.fbq('track', 'Lead', {
                        content_name: 'Free AI Consult',
                        content_category: 'HubSpot Booking',
                    });
                    report.pixelSent = true;
                }

                // Report booking to our API with UTM data (best-effort)
                try {
                    const utmRaw = sessionStorage.getItem('roofclaw_utm');
                    const utmData = utmRaw ? JSON.parse(utmRaw) : {};

                    // K&K ad click, if any. The email is sent only with a click; the
                    // server passes it to K&K's relay, which hashes it for Meta.
                    const knkData = knkBookingFields(
                        e.data?.meetingsPayload?.bookingResponse?.postResponse?.contact?.email
                    );
                    if (knkData?.knk_cid) report.pendingKnk = knkData;
                    report.id ||= crypto.randomUUID();
                    report.bookedAt ||= new Date().toISOString();

                    // Serialize confirmations so a pending write is never treated
                    // as successful. Failed writes may retry on a later message;
                    // the stable UUID prevents duplicates after a lost response.
                    report.queue = report.queue.then(async () => {
                        if (report.recorded && !report.pendingKnk) return;
                        const attribution = report.pendingKnk;
                        const response = await fetch('/api/track-booking', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                ...utmData,
                                booking_id: report.id,
                                booked_at: report.bookedAt,
                                ...(attribution || {}),
                                ...(report.recorded ? { attribution_only: true } : {}),
                            }),
                        });
                        const result = await response.json();
                        if (!response.ok || result.ok !== true || result.warning) {
                            throw new Error('Booking analytics write failed');
                        }
                        report.recorded = true;
                        if (report.pendingKnk === attribution) report.pendingKnk = null;
                    }).catch(() => {
                        console.warn('[track-booking] Analytics write failed; a later confirmation may retry');
                    });
                } catch { /* Attribution is best-effort; the booking remains confirmed. */ }
            }
        };

        window.addEventListener('message', handleHubSpotEvent);
        return () => window.removeEventListener('message', handleHubSpotEvent);
    }, []);

    return (
        <div style={{
            minHeight: '100vh',
            background: '#21255C',
            color: '#FFFFFF',
            fontFamily: "'Sora', sans-serif",
            padding: '0',
            margin: '0',
        }}>
            {/* Header — minimal, no nav */}
            <header style={{
                padding: '24px 32px',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                textAlign: 'center',
            }}>
                <img
                    src="/favicon.png"
                    alt="RoofClaw"
                    style={{ height: '40px', opacity: 0.95 }}
                />
            </header>

            <main style={{ maxWidth: '760px', margin: '0 auto', padding: '48px 24px' }}>
                {/* Video placeholder */}
                <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '2px solid rgba(255,102,51,0.4)',
                    borderRadius: '12px',
                    aspectRatio: '16/9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '40px',
                    overflow: 'hidden',
                }}>
                    {/* Swap src= with the actual video URL when Adam records it */}
                    {/* Example: <iframe src="https://www.youtube.com/embed/VIDEO_ID" ... /> */}
                    <div style={{ textAlign: 'center', opacity: 0.5, padding: '32px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>▶</div>
                        <div style={{ fontSize: '14px', color: '#FF6633' }}>
                            Video coming soon — Adam records this weekend
                        </div>
                    </div>
                </div>

                {/* Headline */}
                <h1 style={{
                    fontSize: 'clamp(28px, 5vw, 42px)',
                    fontWeight: '700',
                    lineHeight: '1.2',
                    marginBottom: '24px',
                    textAlign: 'center',
                }}>
                    Your AI-powered roofing business starts here.
                </h1>

                {/* 3 bullets */}
                <ul style={{
                    listStyle: 'none',
                    padding: '0',
                    margin: '0 0 40px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                }}>
                    {[
                        'Your data stays on your machine',
                        'Works with your existing CRM',
                        'Full setup in 3 hours',
                    ].map((point, i) => (
                        <li key={i} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            fontSize: '18px',
                            fontWeight: '500',
                        }}>
                            <span style={{
                                color: '#FF6633',
                                fontSize: '22px',
                                flexShrink: 0,
                            }}>✅</span>
                            {point}
                        </li>
                    ))}
                </ul>

                {/* Testimonial */}
                <blockquote style={{
                    background: 'rgba(255,102,51,0.1)',
                    border: '1px solid rgba(255,102,51,0.3)',
                    borderLeft: '4px solid #FF6633',
                    borderRadius: '8px',
                    padding: '24px 28px',
                    margin: '0 0 48px 0',
                    fontStyle: 'italic',
                    fontSize: '16px',
                    lineHeight: '1.7',
                    color: 'rgba(255,255,255,0.9)',
                }}>
                    "A 15-man roofing crew in Florida set up their AI agent on a Thursday. By Saturday,
                    it had handled 3 follow-up calls, drafted 2 proposals, and scheduled a crew without
                    anyone touching their phone."
                    <footer style={{
                        marginTop: '12px',
                        fontStyle: 'normal',
                        fontSize: '13px',
                        color: '#FF6633',
                        fontWeight: '600',
                    }}>
                        — RoofClaw customer (name withheld)
                    </footer>
                </blockquote>

                {/* CTA section */}
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <h2 style={{
                        fontSize: '24px',
                        fontWeight: '600',
                        marginBottom: '8px',
                    }}>
                        Book your free 15-minute discovery call
                    </h2>
                    <p style={{
                        color: 'rgba(255,255,255,0.65)',
                        fontSize: '15px',
                        marginBottom: '32px',
                    }}>
                        No pitch deck. No upsell. Just an honest conversation about whether RoofClaw fits your operation.
                    </p>
                </div>

                {/* HubSpot Meetings embed */}
                <div style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    minWidth: '320px',
                    height: '700px',
                }}>
                    <iframe
                        ref={meetingsFrame}
                        src={`${HUBSPOT_MEETING_URL}?embed=true`}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        title="Book a free AI consult with Adam"
                        style={{ border: 'none', display: 'block' }}
                    />
                </div>
            </main>

            {/* Minimal footer — no links, just trust signals */}
            <footer style={{
                textAlign: 'center',
                padding: '32px 24px',
                borderTop: '1px solid rgba(255,255,255,0.08)',
                fontSize: '12px',
                color: 'rgba(255,255,255,0.35)',
            }}>
                © {new Date().getFullYear()} RoofClaw Systems · roofclaw.com
                <br />
                Your information is never sold or shared.
            </footer>
        </div>
    );
};

export default DemoPage;
